import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../../Utils/persistHelpers"
import { defaultFiltersByFolders } from "../../../../Utils/utils"
import { logout } from "../../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../../User/redux/user.actions"
import { REQUESTS_ENROLLMENTS_SLICE_NAME } from "./requestsEnrollments.constants"
import {
  bulkUpdateRequestsEnrollment,
  deleteRequestsEnrollment,
  getRequestsEnrollments,
  getRequestsEnrollmentsExport,
  getRequestsEnrollmentsFilterOptions,
  updateRequestsEnrollment
} from "./requestsEnrollments.thunks"

/* ------------- Initial State ------------- */

const columnsAdmin = [
  { field_id: "created_timestamp", label: "Request date", active: true },
  { field_id: "updated_timestamp", label: "Update status date", active: true },
  //{field_id: 'request_type', label: 'Request type', active: true},
  { field_id: "is_use_another_solutions", label: "Using Competitor", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "enrollments", label: "Enrollments", active: true },
  { field_id: "merchant_descriptor", label: "Billing descriptor", active: true },
  { field_id: "visa_arn_code_date", label: "ARN Added Date", active: true },
  { field_id: "arn_codes", label: "ARN codes", active: true },
  { field_id: "bin_codes", label: "BIN codes", active: false },
  { field_id: "caid_codes", label: "CAID codes", active: false },
  { field_id: "balance_current", label: "Current Balance", active: true },
  { field_id: "another_solution_name", label: "Previous solution", active: false },
  { field_id: "previously_connected_enrollments", label: "Previously enrolled", active: false },
  {
    field_id: "is_user_notified_previous_solution",
    label: "Previous partner notified",
    active: false
  },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true },
  { field_id: "business_url", label: "Business URL", active: true },

  { field_id: "admin_comment", label: "Comment", active: true },
  { field_id: "admin_screenshots", label: "Screenshots", active: true }
]

const initialState = {
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumnsAdmin: [{ field_id: "request_status", label: "Status" }, ...columnsAdmin],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    request_status: "all",
    created_timestamp: null,
    updated_timestamp: null,
    visa_arn_code_date: null,
    //request_type: [],
    is_use_another_solutions: "all",
    enrollments: [],
    merchant_descriptor: "all",
    arn_codes: [],
    another_solution_name: [],
    previously_connected_enrollments: [],
    is_user_notified_previous_solution: "all",
    user_full_name: [],
    user_email: [],
    business_account: [],
    business_url: [],
    exclude_fields: [],
    is_archived: null,
    admin_comment: [],
    admin_screenshots: "all",
    balance_current: { min: null, max: null, type: null }
  },

  fetchingFilterOptions: false,
  filterOptions: {
    request_status: [],
    admin_comment: []
  },

  visibleFiltersAdmin: {
    request_status: true,
    user_email: true,
    user_full_name: true,
    business_account: true
  },

  exportFetching: false,
  exportResult: null,

  saveResult: null
}

/* ------------- Shared reducer cases ------------- */

// Legacy `requestsEnrollmentsFailure` backed the list/update/delete/bulk-update failure
// cases and always just cleared `fetching`.
const requestsEnrollmentsFailure = (state) => {
  state.fetching = false
}

const slice = createSlice({
  name: REQUESTS_ENROLLMENTS_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments —
    // `requestsEnrollmentsSetProp(key, value)`. RTK actions carry a single payload, so
    // callers pass `requestsEnrollmentsSetProp({ key, value })`, matching the
    // `businessAccountsSetProp`/`adminUsersSetProp` slices.
    requestsEnrollmentsSetProp(state, action) {
      const { key, value } = action.payload

      if (["filters", "visibleFilters", "visibleFiltersAdmin"]?.includes(key)) {
        if (key === "filters") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state?.[key], ...value }
      } else {
        state[key] = value
      }
    },
    clearRequestsEnrollmentsFilters(state, action) {
      state.filters = { ...initialState.filters, ...action.payload }
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- filter options ---------------- */
      .addCase(getRequestsEnrollmentsFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(getRequestsEnrollmentsFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = action.payload
      })
      .addCase(getRequestsEnrollmentsFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      /* ---------------- list ---------------- */
      .addCase(getRequestsEnrollments.pending, (state) => {
        state.fetching = true
      })
      .addCase(getRequestsEnrollments.fulfilled, (state, action) => {
        const result = action.payload

        state.fetching = false
        if (result.results) {
          state.result = result
        } else {
          state.result = {
            count: result?.length || 0,
            results: result
          }
        }
      })
      .addCase(getRequestsEnrollments.rejected, requestsEnrollmentsFailure)

      /* ---------------- update ---------------- */
      .addCase(updateRequestsEnrollment.pending, (state) => {
        state.fetching = true
      })
      .addCase(updateRequestsEnrollment.fulfilled, (state, action) => {
        const enrollment = action.payload
        let isChangedArchived = false
        const newResults = state.result.results?.map((el) => {
          if (el.id === enrollment.id) {
            if (el.is_archived !== enrollment.is_archived) isChangedArchived = true
            return { ...el, request_status: enrollment.request_status }
          }
          return el
        })

        state.fetching = false
        state.result = {
          ...state.result,
          results: newResults
        }
        state.saveResult = isChangedArchived
          ? enrollment.is_archived
            ? "archived"
            : "unarchived"
          : "updated"
      })
      .addCase(updateRequestsEnrollment.rejected, requestsEnrollmentsFailure)

      /* ---------------- delete ---------------- */
      .addCase(deleteRequestsEnrollment.pending, (state) => {
        state.fetching = true
      })
      .addCase(deleteRequestsEnrollment.fulfilled, (state, action) => {
        const deleted = action.payload

        state.fetching = false
        state.result = {
          count: state.result.count - 1,
          results: state.result.results?.filter((el) => el.id !== deleted.id)
        }
      })
      .addCase(deleteRequestsEnrollment.rejected, requestsEnrollmentsFailure)

      /* ---------------- bulk update ---------------- */
      .addCase(bulkUpdateRequestsEnrollment.pending, (state) => {
        state.fetching = true
      })
      .addCase(bulkUpdateRequestsEnrollment.fulfilled, (state) => {
        state.fetching = false
      })
      .addCase(bulkUpdateRequestsEnrollment.rejected, requestsEnrollmentsFailure)

      /* ---------------- export ---------------- */
      .addCase(getRequestsEnrollmentsExport.pending, (state) => {
        state.exportFetching = true
      })
      .addCase(getRequestsEnrollmentsExport.fulfilled, (state, action) => {
        state.exportFetching = false
        state.exportResult = action.payload
      })
      .addCase(getRequestsEnrollmentsExport.rejected, (state) => {
        state.exportFetching = false
      })

      /* ---------------- logout ---------------- */
      .addCase(logout, (state) => ({
        ...initialState,
        filters: state.filters,
        columnsAdmin: state.columnsAdmin,
        visibleFiltersAdmin: state.visibleFiltersAdmin
      }))

      /* ---------------- restore saved preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["requests-enrollments"]?.data
        if (!preference || preference.version !== REQUESTS_ENROLLMENTS_PREFERENCES_VERSION) return
        if (!preference.filters || !preference.columnsAdmin || !preference.visibleFiltersAdmin)
          return

        state.filters = preference.filters
        state.columnsAdmin = preference.columnsAdmin
        state.visibleFiltersAdmin = preference.visibleFiltersAdmin
      })
  }
})

export const requestsEnrollmentsActions = slice.actions

// Bump when the shape of the saved `{ version, filters, columnsAdmin, visibleFiltersAdmin }`
// payload changes.
export const REQUESTS_ENROLLMENTS_PREFERENCES_VERSION = 1

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state, folder) => {
  const defaultFilters = defaultFiltersByFolders.enrollments[folder] || initialState.filters

  const filtersToCompare = {
    ...initialState.filters,
    request_status: defaultFilters.request_status,
    is_archived: defaultFilters.is_archived
  }

  return isEqual(filtersToCompare, state.requestsEnrollments.filters)
}

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "requestsEnrollments",
    storage,
    // Only the user's own table setup survives a reload - the list, filter options, and
    // export result are all re-fetched on mount.
    whitelist: ["filters", "filtersLastUpd", "columnsAdmin", "visibleFiltersAdmin"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
